import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import Logo from "@/components/Logo";
import {
  SignOut,
  ChartLineUp,
  Calendar,
  Ticket,
  CurrencyInr,
  CheckCircle,
  XCircle,
  Package,
  ChatCircleText,
  PencilSimple,
  Trash,
  Plus,
} from "@phosphor-icons/react";

const TABS = [
  { key: "overview", label: "Overview", icon: ChartLineUp },
  { key: "bookings", label: "Bookings", icon: Ticket },
  { key: "packages", label: "Packages", icon: Package },
  { key: "contacts", label: "Contacts", icon: ChatCircleText },
];

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("overview");
  const [stats, setStats] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [packages, setPackages] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    if (!localStorage.getItem("mg_admin_token")) {
      navigate("/admin/login");
      return;
    }
    loadAll();
  }, []);

  const loadAll = async () => {
    try {
      const [s, b, p, c] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/bookings"),
        api.get("/admin/packages"),
        api.get("/admin/contacts"),
      ]);
      setStats(s.data);
      setBookings(b.data);
      setPackages(p.data);
      setContacts(c.data);
    } catch (e) {
      if (e.response?.status === 401) {
        localStorage.removeItem("mg_admin_token");
        navigate("/admin/login");
      }
    }
  };

  const logout = () => {
    localStorage.removeItem("mg_admin_token");
    navigate("/admin/login");
  };

  const updateStatus = async (id, status) => {
    try {
      await api.put(`/admin/bookings/${id}/status`, { status });
      toast.success(`Booking ${status}`);
      loadAll();
    } catch (e) {
      toast.error("Failed to update");
    }
  };

  const savePackage = async (pkg, isNew) => {
    try {
      if (isNew) await api.post("/admin/packages", pkg);
      else await api.put(`/admin/packages/${pkg.id}`, pkg);
      toast.success("Saved");
      loadAll();
    } catch (e) {
      toast.error("Failed");
    }
  };

  const deletePackage = async (id) => {
    if (!window.confirm("Delete this package?")) return;
    await api.delete(`/admin/packages/${id}`);
    toast.success("Deleted");
    loadAll();
  };

  const filtered = bookings.filter((b) => filter === "all" || b.status === filter);

  return (
    <div className="min-h-screen bg-mg text-white flex">
      {/* Sidebar */}
      <aside className="w-64 shrink-0 border-r border-mg bg-mg-2 min-h-screen p-6 sticky top-0 h-screen flex flex-col">
        <div className="flex items-center gap-3">
          <Logo size={40} />
          <div>
            <div className="font-display text-sm">
              <span className="gold-gradient-text">Moon</span>
              <span className="silver-gradient-text">Glow</span>
            </div>
            <div className="label-tiny text-[9px] mt-0.5">Admin Console</div>
          </div>
        </div>
        <nav className="mt-10 flex-1 space-y-1">
          {TABS.map((t) => (
            <button
              key={t.key}
              data-testid={`admin-tab-${t.key}`}
              onClick={() => setTab(t.key)}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-colors ${
                tab === t.key ? "text-gold border" : "text-white/70 hover:text-white"
              }`}
              style={tab === t.key ? { backgroundColor: "rgba(212,175,55,0.08)", borderColor: "var(--mg-gold-border)" } : {}}
            >
              <t.icon size={18} weight="duotone" />
              {t.label}
            </button>
          ))}
        </nav>

        <div className="text-[11px] text-white/40">
          Signed in as
          <div className="text-white/70 mt-0.5" data-testid="admin-email-label">{localStorage.getItem("mg_admin_email")}</div>
        </div>
        <button
          data-testid="admin-logout"
          onClick={logout}
          className="btn-ghost-gold rounded-md mt-4 px-3 py-2 text-xs inline-flex items-center justify-center gap-2 w-full"
        >
          <SignOut size={14} /> Sign out
        </button>
      </aside>

      {/* Main */}
      <main className="flex-1 p-10 max-w-[1400px]">
        {tab === "overview" && (
          <div data-testid="admin-overview">
            <h1 className="font-display text-4xl">Overview</h1>
            <p className="text-white/60 mt-2 text-sm">Live snapshot of celebrations at MoonGlow.</p>
            <div className="grid md:grid-cols-4 gap-4 lg:gap-6 mt-8">
              <Stat k="Total Bookings" v={stats?.total ?? "—"} icon={Ticket} />
              <Stat k="Upcoming" v={stats?.upcoming ?? "—"} icon={Calendar} />
              <Stat k="Confirmed" v={stats?.confirmed ?? "—"} icon={CheckCircle} />
              <Stat k="Revenue Locked" v={stats ? `₹${Number(stats.total_revenue).toLocaleString("en-IN")}` : "—"} icon={CurrencyInr} gold />
            </div>

            <div className="mt-12">
              <div className="label-tiny">Recent bookings</div>
              <div className="mt-4 card-lux rounded-lg overflow-hidden">
                <BookingTable rows={bookings.slice(0, 8)} onStatus={updateStatus} />
              </div>
            </div>
          </div>
        )}

        {tab === "bookings" && (
          <div data-testid="admin-bookings">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div>
                <h1 className="font-display text-4xl">Bookings</h1>
                <p className="text-white/60 mt-2 text-sm">Manage every celebration.</p>
              </div>
              <div className="flex gap-2 flex-wrap">
                {["all", "pending", "confirmed", "cancelled", "completed"].map((f) => (
                  <button
                    key={f}
                    data-testid={`filter-${f}`}
                    onClick={() => setFilter(f)}
                    className={`px-4 py-2 rounded-md text-xs uppercase tracking-widest transition-colors ${
                      filter === f ? "border text-gold" : "border border-transparent text-white/60 hover:text-white"
                    }`}
                    style={filter === f ? { backgroundColor: "rgba(212,175,55,0.08)", borderColor: "var(--mg-gold-border)" } : {}}
                  >
                    {f}
                  </button>
                ))}
              </div>
            </div>
            <div className="mt-8 card-lux rounded-lg overflow-hidden">
              <BookingTable rows={filtered} onStatus={updateStatus} full />
            </div>
          </div>
        )}

        {tab === "packages" && (
          <PackagesPanel packages={packages} onSave={savePackage} onDelete={deletePackage} />
        )}

        {tab === "contacts" && (
          <div data-testid="admin-contacts">
            <h1 className="font-display text-4xl">Contact messages</h1>
            <div className="mt-8 grid gap-4">
              {contacts.length === 0 && <div className="text-white/50">No messages yet.</div>}
              {contacts.map((c) => (
                <div key={c.id} className="card-lux rounded-lg p-6">
                  <div className="flex items-baseline justify-between">
                    <div className="font-display text-lg">{c.name}</div>
                    <div className="label-tiny">{new Date(c.created_at).toLocaleString()}</div>
                  </div>
                  <div className="text-xs text-white/60 mt-1">
                    {c.phone} · {c.email}
                  </div>
                  <p className="mt-4 text-sm text-white/85 leading-relaxed">{c.message}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

function Stat({ k, v, icon: Icon, gold }) {
  return (
    <div className="card-lux rounded-lg p-6">
      <div className="flex items-center justify-between">
        <div className="label-tiny">{k}</div>
        <Icon size={18} className="text-gold" weight="duotone" />
      </div>
      <div className={`mt-4 font-display text-3xl ${gold ? "gold-gradient-text" : ""}`}>{v}</div>
    </div>
  );
}

function BookingTable({ rows, onStatus, full }) {
  const badge = (s) => {
    const map = {
      pending: "border-yellow-500/40 text-yellow-400",
      confirmed: "border-green-500/40 text-green-400",
      cancelled: "border-red-500/40 text-red-400",
      completed: "border-blue-500/40 text-blue-400",
    };
    return `border rounded-full px-2.5 py-0.5 text-[10px] uppercase tracking-widest ${map[s] || ""}`;
  };
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-white/50 border-b border-mg">
            <th className="px-5 py-3 font-normal label-tiny">Ref</th>
            <th className="px-5 py-3 font-normal label-tiny">Customer</th>
            <th className="px-5 py-3 font-normal label-tiny">Occasion</th>
            <th className="px-5 py-3 font-normal label-tiny">Date · Slot</th>
            <th className="px-5 py-3 font-normal label-tiny">Amount</th>
            <th className="px-5 py-3 font-normal label-tiny">Status</th>
            {full && <th className="px-5 py-3 font-normal label-tiny">Actions</th>}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && (
            <tr>
              <td colSpan={7} className="px-5 py-8 text-center text-white/40">No bookings yet.</td>
            </tr>
          )}
          {rows.map((b) => (
            <tr key={b.id} data-testid={`booking-row-${b.reference}`} className="border-b border-mg/50 hover:bg-white/[0.02]">
              <td className="px-5 py-4 font-mono text-gold">{b.reference}</td>
              <td className="px-5 py-4">
                <div>{b.customer_name}</div>
                <div className="text-xs text-white/50">{b.phone}</div>
              </td>
              <td className="px-5 py-4 capitalize">{b.occasion}</td>
              <td className="px-5 py-4">
                <div>{b.booking_date}</div>
                <div className="text-xs text-white/50">{b.slot}</div>
              </td>
              <td className="px-5 py-4">
                <div>₹{Number(b.total_amount).toLocaleString("en-IN")}</div>
                <div className="text-xs text-white/50">Adv ₹{Number(b.advance_amount).toLocaleString("en-IN")}</div>
              </td>
              <td className="px-5 py-4"><span className={badge(b.status)}>{b.status}</span></td>
              {full && (
                <td className="px-5 py-4">
                  <div className="flex gap-2 flex-wrap">
                    <button
                      data-testid={`confirm-${b.reference}`}
                      onClick={() => onStatus(b.id, "confirmed")}
                      className="btn-ghost-gold rounded-md px-3 py-1.5 text-xs inline-flex items-center gap-1"
                    >
                      <CheckCircle size={12} /> Confirm
                    </button>
                    <button
                      data-testid={`complete-${b.reference}`}
                      onClick={() => onStatus(b.id, "completed")}
                      className="btn-ghost-gold rounded-md px-3 py-1.5 text-xs"
                    >
                      Complete
                    </button>
                    <button
                      data-testid={`cancel-${b.reference}`}
                      onClick={() => onStatus(b.id, "cancelled")}
                      className="btn-ghost-gold rounded-md px-3 py-1.5 text-xs inline-flex items-center gap-1"
                    >
                      <XCircle size={12} /> Cancel
                    </button>
                  </div>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function PackagesPanel({ packages, onSave, onDelete }) {
  const empty = { id: null, name: "", description: "", price: 0, image_url: "", is_active: true };
  const [editing, setEditing] = useState(null);

  return (
    <div data-testid="admin-packages">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-4xl">Decoration Packages</h1>
          <p className="text-white/60 mt-2 text-sm">Create, edit and toggle themed packages.</p>
        </div>
        <button
          data-testid="new-package-btn"
          onClick={() => setEditing({ ...empty })}
          className="btn-gold rounded-md px-4 py-2 text-sm inline-flex items-center gap-2"
        >
          <Plus size={16} weight="bold" /> New Package
        </button>
      </div>

      <div className="mt-8 grid md:grid-cols-2 xl:grid-cols-3 gap-6">
        {packages.map((p) => (
          <div key={p.id} className="card-lux rounded-lg overflow-hidden">
            <div className="aspect-[16/9] overflow-hidden bg-black">
              {p.image_url && <img src={p.image_url} alt={p.name} className="w-full h-full object-cover" />}
            </div>
            <div className="p-5">
              <div className="flex items-center justify-between">
                <div className="font-display text-lg">{p.name}</div>
                <div className="font-display gold-gradient-text">₹{p.price.toLocaleString("en-IN")}</div>
              </div>
              <div className="text-xs text-white/60 mt-2 leading-relaxed line-clamp-3">{p.description}</div>
              <div className="mt-5 flex gap-2">
                <button
                  data-testid={`edit-pkg-${p.id}`}
                  onClick={() => setEditing({ ...p })}
                  className="btn-ghost-gold rounded-md px-3 py-1.5 text-xs inline-flex items-center gap-1"
                >
                  <PencilSimple size={12} /> Edit
                </button>
                <button
                  data-testid={`delete-pkg-${p.id}`}
                  onClick={() => onDelete(p.id)}
                  className="btn-ghost-gold rounded-md px-3 py-1.5 text-xs inline-flex items-center gap-1 border-red-500/40 text-red-400"
                >
                  <Trash size={12} /> Delete
                </button>
                <div className="ml-auto label-tiny">{p.is_active ? "ACTIVE" : "INACTIVE"}</div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-md flex items-center justify-center p-6">
          <div className="card-lux rounded-lg p-8 w-full max-w-lg" style={{ borderColor: "var(--mg-gold)" }}>
            <div className="font-display text-2xl">{editing.id ? "Edit package" : "New package"}</div>
            <div className="mt-6 space-y-4">
              <div>
                <label className="label-tiny">Name</label>
                <input
                  data-testid="pkg-name"
                  value={editing.name}
                  onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                  className="field mt-2 rounded-md px-4 py-3 w-full"
                />
              </div>
              <div>
                <label className="label-tiny">Description</label>
                <textarea
                  data-testid="pkg-desc"
                  value={editing.description}
                  onChange={(e) => setEditing({ ...editing, description: e.target.value })}
                  className="field mt-2 rounded-md px-4 py-3 w-full min-h-[90px]"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label-tiny">Price (₹)</label>
                  <input
                    data-testid="pkg-price"
                    type="number"
                    value={editing.price}
                    onChange={(e) => setEditing({ ...editing, price: parseFloat(e.target.value) || 0 })}
                    className="field mt-2 rounded-md px-4 py-3 w-full"
                  />
                </div>
                <div>
                  <label className="label-tiny">Active</label>
                  <select
                    data-testid="pkg-active"
                    value={editing.is_active ? "1" : "0"}
                    onChange={(e) => setEditing({ ...editing, is_active: e.target.value === "1" })}
                    className="field mt-2 rounded-md px-4 py-3 w-full"
                  >
                    <option value="1">Active</option>
                    <option value="0">Inactive</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="label-tiny">Image URL</label>
                <input
                  data-testid="pkg-img"
                  value={editing.image_url}
                  onChange={(e) => setEditing({ ...editing, image_url: e.target.value })}
                  className="field mt-2 rounded-md px-4 py-3 w-full"
                />
              </div>
            </div>
            <div className="mt-8 flex gap-3 justify-end">
              <button onClick={() => setEditing(null)} className="btn-ghost-gold rounded-md px-4 py-2 text-sm">Cancel</button>
              <button
                data-testid="pkg-save"
                onClick={() => { onSave(editing, !editing.id); setEditing(null); }}
                className="btn-gold rounded-md px-5 py-2 text-sm"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
